import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'bootstrap'
import toaster from '../../../Utils/toaster'
import {CommonLoading} from '../../../Utils/commonLoading'
import AddOSIDetails from '../appointment-management/components/AddOSIDetails'
import type {BatchItem} from './bulkVosTypes'
import {
  applyDigitalVosClientDefaultsToInitial,
  fetchOsiEditContext,
  resolveBulkVosAppointmentCode,
} from './bulkVosOsiEditUtils'

export const BULK_VOS_OSI_MODAL_ID = 'kt_bulk_vos_osi_details'

type Props = {
  item: BatchItem | null
  onClose: () => void
  onReload: () => Promise<void>
}

export function BulkVosOsiEditLauncher({item, onClose, onReload}: Props) {
  const [initialLoading, setInitialLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [appointmentDetails, setAppointmentDetails] = useState<any>(null)
  const [meetingSummary, setMeetingSummary] = useState<any>(null)
  const [initialValuesOSI, setInitialValuesOSI] = useState<any>({})
  const [expenseList, setExpenseList] = useState<any[]>([])

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const itemVosModeRef = useRef<number | undefined>(undefined)
  itemVosModeRef.current =
    item?.vosSubmissionMode != null ? Number(item.vosSubmissionMode) : undefined

  const editItemRef = useRef<BatchItem | null>(null)
  editItemRef.current = item

  const editItemId = item?.id ?? null

  const appointmentCode = useMemo(
    () => (item ? resolveBulkVosAppointmentCode(item) : NaN),
    [item],
  )

  const loadContext = useCallback(async (code: number) => {
    const ctx = await fetchOsiEditContext(code)
    setAppointmentDetails(ctx.appointmentDetails)
    setMeetingSummary(ctx.meetingSummary)
    let initial: Record<string, unknown> = {...ctx.initialValuesOSI}
    if (
      (initial.vosSubmissionMode === undefined || initial.vosSubmissionMode === null) &&
      itemVosModeRef.current != null
    ) {
      initial.vosSubmissionMode = itemVosModeRef.current
    }
    initial = applyDigitalVosClientDefaultsToInitial(
      initial,
      ctx.meetingSummary,
      editItemRef.current,
      ctx.appointmentDetails,
    )
    setInitialValuesOSI(initial)
    setExpenseList(ctx.expenseList)
    return ctx
  }, [])

  useEffect(() => {
    if (!item || editItemId == null) {
      setReady(false)
      setInitialLoading(false)
      return
    }

    if (!Number.isInteger(appointmentCode) || appointmentCode <= 0) {
      toaster('error', 'Missing appointment for this record')
      onCloseRef.current()
      return
    }

    let cancelled = false
    setInitialLoading(true)
    setReady(false)

    void (async () => {
      try {
        await loadContext(appointmentCode)
        if (cancelled) return
        setReady(true)
        requestAnimationFrame(() => {
          const el = document.getElementById(BULK_VOS_OSI_MODAL_ID)
          if (el) Modal.getOrCreateInstance(el).show()
        })
      } catch {
        if (!cancelled) {
          toaster('error', 'Unable to load VOS details')
          onCloseRef.current()
        }
      } finally {
        if (!cancelled) setInitialLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [editItemId, appointmentCode, loadContext])

  useEffect(() => {
    const el = document.getElementById(BULK_VOS_OSI_MODAL_ID)
    if (!el || !ready) return

    const onHidden = () => {
      setReady(false)
      onCloseRef.current()
    }
    el.addEventListener('hidden.bs.modal', onHidden)
    return () => el.removeEventListener('hidden.bs.modal', onHidden)
  }, [ready])

  const fetchAfterSubmitOSISubmmission = useCallback(async () => {
    if (!Number.isInteger(appointmentCode) || appointmentCode <= 0) return
    try {
      await loadContext(appointmentCode)
      await onReload()
    } catch {
      toaster('error', 'Unable to refresh VOS details')
    }
  }, [appointmentCode, loadContext, onReload])

  const hourlyRate =
    appointmentDetails?.rate ??
    appointmentDetails?.interpreterRate ??
    null

  return (
    <div className='position-relative' aria-hidden={!item}>
      {initialLoading && item && !ready ? <CommonLoading /> : null}
      {item && ready ? (
        <AddOSIDetails
          modalElementId={BULK_VOS_OSI_MODAL_ID}
          appointmentCodeOverride={appointmentCode}
          initialValues={initialValuesOSI}
          fetchAfterSubmitOSISubmmission={fetchAfterSubmitOSISubmmission}
          expenseList={expenseList}
          setExpenseList={setExpenseList}
          isOsiEdit
          readOnly={false}
          appointmentDetails={appointmentDetails}
          payableHours={appointmentDetails?.payableHours}
          hourlyRate={hourlyRate}
          meetingSummaryStatus={meetingSummary?.status}
          meetingSummary={meetingSummary}
          forceDigitalVosEditLayout
          defaultDigitalVosSource='client'
        />
      ) : null}
    </div>
  )
}
